import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { roundStringNumberToInt } from "../../../../../AssetManagement/Overview/BusinessLeaderDashboard/helpers/businessleaderDashboardHelpers";

export interface OverviewAccountSummaryColumn {
  key: string;
  label: string;
}

const PERCENT_KEYS = new Set([
  "cvr",
  "cvr_without_incent",
  "roi_perc",
  "ng_ftd_br_ftd",
]);
const NON_CURRENCY_KEYS = new Set([
  "date",
  "br_ftd",
  "ng_ftd",
  "qftds",
  "click_in",
  "click_out",
  "ftd_incent",
  "reg",
]);

const isEmptyValue = (value: any) =>
  value === null || value === undefined || value === "";

const isPercentMetric = (column: OverviewAccountSummaryColumn) =>
  PERCENT_KEYS.has(column.key) || column.label.includes("%");

const isCurrencyMetric = (column: OverviewAccountSummaryColumn) =>
  !NON_CURRENCY_KEYS.has(column.key) && !isPercentMetric(column);

const formatNumber = (value: any) => roundStringNumberToInt(value);

const formatPercent = (value: any) => {
  if (typeof value === "string" && value.includes("%")) return value;
  return `${formatNumber(Number(value) * 100)}%`;
};

const formatCurrency = (value: any) => {
  if (typeof value === "string" && value.includes("$")) return value;
  const formatedNumder =
    value < 0
      ? `-$${formatNumber(Math.abs(value))}`
      : `$${formatNumber(Math.abs(value))}`;
  return formatedNumder;
};

export const getOverviewAccountSummaryColumnLabel = (
  column: OverviewAccountSummaryColumn,
  selectedTimeframeLabel: string
) => {
  if (column.key !== "date") return column.label;
  if (selectedTimeframeLabel === "Last 30 Days") return "Day";
  if (selectedTimeframeLabel === "Last 6 Weeks") return "Week";
  if (
    selectedTimeframeLabel === "Last 12 Months Full" ||
    selectedTimeframeLabel === "Last 12 Months Proportional"
  ) {
    return "Month";
  }
  return column.label;
};

export const formatOverviewAccountSummaryValue = (
  column: OverviewAccountSummaryColumn,
  row: any,
  emptyFallback = "N/A"
) => {
  if (column.key === "date") {
    if (row?.date) {
      return `${row?.day ?? ""} ${dayjs(row.date).format("DD-MM-YYYY")}`.trim();
    }
    if (row?.start_of_week) return row.start_of_week;
    if (row?.month) return row.month;
    return emptyFallback;
  }
  const value = row[column.key];
  if (isEmptyValue(value)) {
    return emptyFallback;
  }
  if (isPercentMetric(column)) {
    return formatPercent(value);
  }
  if (isCurrencyMetric(column)) {
    return formatCurrency(value);
  }
  if (typeof value === "number") {
    return formatNumber(value);
  }
  return value;
};

const escapeCsvCell = (value: any) => {
  const stringValue = String(value ?? "");
  if (/[",\n\r]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

interface ExportOverviewAccountSummaryOptions {
  columns: OverviewAccountSummaryColumn[];
  rows: any[];
  filename?: string;
}

export const exportOverviewAccountSummaryToCSV = ({
  columns,
  rows,
  filename = "OverviewAccountSummary",
}: ExportOverviewAccountSummaryOptions) => {
  const csvRows = [
    columns.map((column) => escapeCsvCell(column.key)).join(","),
    ...rows.map((row) =>
      columns
        .map((column) =>
          escapeCsvCell(formatOverviewAccountSummaryValue(column, row, "null"))
        )
        .join(",")
    ),
  ];
  const blob = new Blob([csvRows.join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  saveAs(blob, `${filename}.csv`);
};
